import { useState } from 'react';
import { Link } from 'react-router';
import { useIssues, useUpdateIssue } from '@/services/queries';
import type { IssueListQuery, PanelIssue } from '@/types';
import { StatusPill } from '@/shared/ui/status-pill';
import { Button } from '@/shared/ui/button';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/shared/ui/select';
import { Skeleton } from '@/shared/ui/skeleton';
import {
  Table,
  TableBody,
  TableCard,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/shared/ui/table';
import { ErrorState, Pagination } from '@/components/common';
import { formatDateTime } from '@/shared/lib/format';
import { humanise } from '@/lib/format';
import { ROUTES } from '@/constants';
import { RaiseIssueDialog } from './RaiseIssueDialog';
import { SLA_HOURS, slaFor } from './sla';

const SLA_TEXT = {
  caution: 'text-pill-caution-foreground',
  negative: 'text-pill-negative-foreground',
  positive: 'text-pill-positive-foreground',
  inert: 'text-muted-foreground',
} as const;

const PAGE_SIZE = 25;
const ANY = 'any';

const STATUSES = ['open', 'in_progress', 'resolved'] as const;
const SEVERITIES = ['low', 'medium', 'high', 'critical'] as const;

/**
 * Issues raised against the portals.
 *
 * Status is editable inline rather than behind a detail page: the only thing
 * anybody does to an issue here is move it along, and a page load to change one
 * enum is a page load too many.
 *
 * The default filter is `open`, because a list that opens on resolved issues is
 * a list that looks empty when there is work to do.
 */
export function IssueTable() {
  const [status, setStatus] = useState<string>('open');
  const [severity, setSeverity] = useState<string>(ANY);
  const [offset, setOffset] = useState(0);

  const query: IssueListQuery = {
    limit: PAGE_SIZE,
    offset,
    ...(status !== ANY ? { status: status as IssueListQuery['status'] } : {}),
    ...(severity !== ANY ? { severity: severity as IssueListQuery['severity'] } : {}),
  };

  const issues = useIssues(query);
  const update = useUpdateIssue();

  const changeFilter = (setter: (value: string) => void) => (value: string) => {
    setter(value);
    setOffset(0);
  };

  const setIssueStatus = (issue: PanelIssue, next: string) => {
    update.mutate({
      issueId: issue.id,
      body: { status: next as PanelIssue['status'] },
    });
  };

  return (
    <div className="space-y-4">
      <div className="flex flex-wrap items-center gap-3">
        <Select value={status} onValueChange={changeFilter(setStatus)}>
          <SelectTrigger className="w-40" aria-label="Filter by status">
            <SelectValue placeholder="Status" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value={ANY}>All statuses</SelectItem>
            {STATUSES.map((value) => (
              <SelectItem key={value} value={value}>
                {humanise(value)}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>

        <Select value={severity} onValueChange={changeFilter(setSeverity)}>
          <SelectTrigger className="w-40" aria-label="Filter by severity">
            <SelectValue placeholder="Severity" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value={ANY}>All severities</SelectItem>
            {SEVERITIES.map((value) => (
              <SelectItem key={value} value={value}>
                {humanise(value)}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>

        <RaiseIssueDialog />
      </div>

      {issues.isError && (
        <ErrorState
          title="Could not load issues"
          description={issues.error.message}
          action={
            <Button
              variant="outline"
              onClick={() => {
                void issues.refetch();
              }}
            >
              Try again
            </Button>
          }
        />
      )}

      <TableCard>
        <Table>
          <TableHeader>
            <TableRow>
              <TableHead>Subject</TableHead>
              <TableHead className="hidden sm:table-cell w-28">Portal</TableHead>
              <TableHead className="w-28">Severity</TableHead>
              <TableHead
                className="w-32"
                title={`Response window: ${Object.entries(SLA_HOURS)
                  .map(([level, hours]) => `${level} ${String(hours)}h`)
                  .join(', ')}`}
              >
                SLA
              </TableHead>
              <TableHead className="hidden md:table-cell w-44">Raised</TableHead>
              <TableHead className="w-44">Status</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {issues.isPending &&
              Array.from({ length: 6 }, (_, index) => (
                <TableRow key={index}>
                  <TableCell colSpan={6}>
                    <Skeleton className="h-5 w-full" />
                  </TableCell>
                </TableRow>
              ))}

            {issues.data?.data.length === 0 && (
              <TableRow>
                <TableCell colSpan={6} className="py-10 text-center text-muted-foreground">
                  {status === 'open' ? 'Nothing open.' : 'No issues match these filters.'}
                </TableCell>
              </TableRow>
            )}

            {issues.data?.data.map((issue) => (
              <TableRow key={issue.id}>
                <TableCell className="max-w-md">
                  <p className="font-medium">{issue.subject}</p>
                  {issue.crashSignature && (
                    <Link
                      to={ROUTES.crashes}
                      className="mt-0.5 inline-block font-mono text-[11px] text-muted-foreground hover:underline"
                      title="Raised from this crash group"
                    >
                      crash {issue.crashSignature}
                    </Link>
                  )}
                  {issue.description && (
                    <p className="mt-0.5 line-clamp-2 text-sm text-muted-foreground">
                      {issue.description}
                    </p>
                  )}
                </TableCell>
                <TableCell className="hidden sm:table-cell text-muted-foreground">
                  {issue.portal}
                </TableCell>
                <TableCell>
                  <StatusPill status={issue.severity} minWidth={72} />
                </TableCell>
                <TableCell>
                  {(() => {
                    const sla = slaFor(issue);
                    return (
                      <span
                        className={`text-[12.5px] font-semibold tabular-nums ${SLA_TEXT[sla.tone]}`}
                        title={`Due ${formatDateTime(sla.due)}`}
                      >
                        {sla.label}
                      </span>
                    );
                  })()}
                </TableCell>
                <TableCell className="hidden md:table-cell text-muted-foreground tabular-nums">
                  {formatDateTime(issue.createdAt)}
                </TableCell>
                <TableCell>
                  <Select
                    value={issue.status}
                    onValueChange={(value) => {
                      setIssueStatus(issue, value);
                    }}
                    disabled={update.isPending}
                  >
                    <SelectTrigger className="h-8 w-full" aria-label={`Status of ${issue.subject}`}>
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      {STATUSES.map((value) => (
                        <SelectItem key={value} value={value}>
                          {humanise(value)}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                  {issue.resolvedAt && (
                    /* A timestamp, not a status word - so it reads as a note
                       under the control rather than as a second pill. */
                    <p className="mt-1 text-[10.5px] text-muted-foreground">
                      Resolved {formatDateTime(issue.resolvedAt)}
                    </p>
                  )}
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </TableCard>

      {issues.data && (
        <Pagination
          offset={offset}
          pageSize={PAGE_SIZE}
          count={issues.data.data.length}
          total={issues.data.meta.total}
          hasMore={issues.data.meta.hasMore}
          onOffsetChange={setOffset}
        />
      )}
    </div>
  );
}
